import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import "./Chats.css"


class Chats extends React.Component {
  state = {
    lastActiveChannelId: null,
    lastNofItems: null,
    lastItemId: null,
    repeatDialogTriggered: false
  }

  componentDidUpdate() {
    // update scroll position
    this.updateScrollbar()
  }

  componentDidMount() {
    // init scroll position
    this.updateScrollbar()
  }

  componentWillUnmount() {
    // cancel setTimeout from selection
    this.props.unmountChat()
  }

  // check if chat window needs to be updated to avoid unnecessary re-rendering
  shouldComponentUpdate(nextProps, nextState) {
    // always update state changes
    if(this.state !== nextState) {
      return true
    }

    // check for updates in props
    if(nextProps.activeChannel) {
      let messages = this.props.activeChannel.messages
      let nextMessages = nextProps.activeChannel.messages

      // create a re-render for answer and loading spin, otherwise they are not always updated independently
      if(Array.isArray(nextMessages) && nextMessages.length && nextMessages[nextMessages.length-1] && (nextMessages[nextMessages.length-1].id === "answer"  || nextMessages[nextMessages.length-1].id === "loading")) {
        return true
      }

      // update smallDevice variable if view is changed
      if(nextProps.smallDevice !== this.props.smallDevice) {
        return true
      }

      return JSON.stringify(messages) !== JSON.stringify(nextMessages)
    } else {
      return false
    }
  }

  // force re-rendering
  reRender = () => {
    this.forceUpdate()
    this.scrollToPos("end", "smooth", 100)
  }

  updateScrollbar() {
    // check if clicked channel exists and has messages
    if(this.props.activeChannel && Array.isArray(this.props.activeChannel.messages) && this.props.activeChannel.messages.length) {
      let nofItems = this.props.activeChannel.messages.length
      let lastItemId = null
      if(this.props.activeChannel.messages[nofItems-1]) lastItemId = this.props.activeChannel.messages[nofItems-1].id

      // if channel was changed
      if(this.state.lastActiveChannelId !== this.props.activeChannel.id) {
        this.setState({lastActiveChannelId: this.props.activeChannel.id})

        if(this.props.scrollPos === null) {
          // initialize scrollbar with end position
          this.scrollToPos("end", "smooth", 10)
        } else {
          // set scrollbar as it was before
          this.scrollToPos(this.props.scrollPos, "auto", 5)
        }

        this.setState({lastNofItems: nofItems})
        this.setState({lastItemId: lastItemId})
      }
      // if selection has been made
      else if(this.state.lastNofItems !== nofItems) {
        this.setState({lastNofItems: nofItems})
        this.setState({lastItemId: lastItemId})
        this.scrollToPos("end", "smooth", 100)
      }
      // if loading item is replaced
      else if(this.state.lastNofItems === nofItems && this.state.lastItemId === "loading" && lastItemId !== "loading") {
        this.setState({lastItemId: lastItemId})
        this.scrollToPos("end", "smooth", 100)
      }
    }
  }

  scrollToPos(scrollPos, scrollBehavior, scrollTime) {
    let chatDialog = document.getElementById("chatContainer")
    setTimeout(() => {
      if(scrollPos === "end") {
        let offsetMessages = chatDialog.scrollHeight + 1
        let offsetChatDialog = chatDialog.offsetHeight
        scrollPos = offsetMessages - offsetChatDialog
      }
      if(scrollPos >= 0) {
        chatDialog.scroll({
          behavior: scrollBehavior,
          top: scrollPos
        })
      }
    }, scrollTime)
  }


  render() {
    let repeatDialogButton = "repeat-dialog-button"
    if(this.state.repeatDialogTriggered) repeatDialogButton += " repeat-dialog-button-triggered"

    let repeatButton = null
    if (this.props.activeChannel && this.props.activeChannel.farewell) {
      repeatButton =
        <div className="repeat-dialog-button-container">
          <button className={repeatDialogButton} onClick={() => {
              this.setState({repeatDialogTriggered: true})
              this.props.repeatDialog(this.props.activeChannel.id, () => {
                this.setState({repeatDialogTriggered: false})
              })
            }}>
            I want to have this conversation again
          </button>
        </div>
    }
    return (
      <React.Fragment>
        <div className="chat-dialog">
          <Chatdialog activeChannel={this.props.activeChannel} selectOption={this.props.selectOption} channelIndex={this.props.channelIndex} reRender={this.reRender} smallDevice={this.props.smallDevice}/>
        </div>
        {repeatButton &&
          repeatButton
        }
      </React.Fragment>
    )
  }
}


function Chatdialog (props) {
  // use state hook to save us from having to turn the function into a class for using states
  const [queue, setQueue] = useState([])
  const [served, setServed] = useState([])
  const [timeoutQueue, setTimeoutQueue] = useState([])

  // use ref hook to make the content of the timeoutQueue accessible in the useEffect hook
  const timeoutQueueRef = useRef(null);
  timeoutQueueRef.current = timeoutQueue;

  // use effect hook to cleanup timeouts if channel is changed
  useEffect(() => {
    return function cleanup() {
      for(let timeout of timeoutQueueRef.current) {
        clearTimeout(timeout)
      }
    }
  }, [])

  if (props.activeChannel) {
    const messages = props.activeChannel.messages
    if(Array.isArray(messages) && messages.length) {
      let messagesList = []
      let year, dayOfYear = null
      messages.forEach((item, index) => {
        if(item) {
          // create date seperator
          if(item.timestamp) {
            let y = moment(item.timestamp).format("Y")
            let doy = moment(item.timestamp).dayOfYear()
            if(year !== y || dayOfYear !== doy) {
              year = y
              dayOfYear = doy
              messagesList.push(
                <li key={`separator-${item.id}`}>
                  <Dateseparator timestamp={item.timestamp}/>
                </li>
              )
            }
          }

          // dirty fix for bug that delays user responses, please remove if fixed
          if(item.byUser && item.age < 0) {
            item.age = 0
          }

          // check if message is in the past or already ended its delay
          if(item.age < 0 && !served.includes(item.id)) {
            // check if message has already been delayed
            if(!queue.includes(item.id)) {
              setQueue(queue => [...queue, item.id])
              let timeout = setTimeout(() => {
                props.reRender()
                setServed(served => [...served, item.id])
              }, Math.abs(item.age))
              setTimeoutQueue(timeoutQueue => [...timeoutQueue, timeout])
            }
            // check whether the last message is in the present or has already ended its delay
            if(index > 0 && (messages[index-1].age >= 0 || served.includes(messages[index-1].id) )) {
              let loading = {
                type: "text",
                byUser: false,
                text: " ",
                id: "loading",
                timestamp: item.timestamp,
                age: item.age
              }
              messagesList.push(
                <li key={item.id} id={item.id}>
                  <Chatdialogbox item={loading}/>
                </li>
              )
            }
            // hide this message until their time comes
            return
          }

          // create message
          if (item.type === "text" && item.id && item.text)
          messagesList.push(
            <li key={item.id} id={item.id}>
              <Chatdialogbox item={item}/>
            </li>
          )
          else if(item.type === "media" && item.id && item.source)
          messagesList.push(
            <li key={item.id} id={item.id}>
              <Chatdialogmedia item={item} smallDevice={props.smallDevice}/>
            </li>
          )
          else if(item.type === "selection" && item.id && item.text)
          messagesList.push(
            <li key={item.id} id={item.id}>
              <Chatdialogselection item={item} nextItem={(index === messages.length-1) ?  null : messages[index+1]} selectOption={props.selectOption} activeChannel={props.activeChannel} channelIndex={props.channelIndex}/>
            </li>
          )
          else
          console.log("warning: missing content type, id, text or source for media in channel " + props.activeChannel.id )
        }
      })
      return (
        <ul className="chat-dialog-list">
          {messagesList}
        </ul>
      )
    }
  }
  return null
}

const Dateseparator = props => (
  <div className="date-separator">
    <span>
      {moment(props.timestamp).format("D. MMMM YYYY")}
    </span>
  </div>
)

function Chatdialogbox (props) {
  let container = "chat-container-left"
  if(props.item.byUser) {
    container = "chat-container-right"
  }

  let chatItemTime = "chat-item-time"
  if(props.item.id === "loading") chatItemTime += " hide"

  return (
    <div className={container}>
      <div className="chat-item speech-bubble">
        <div className={props.item.id === "loading" ? "chat-item-text loading" : "chat-item-text"}>
          {props.item.text &&
            addHtmlTags(props.item.text)
          }
        </div>
        <div className={chatItemTime}>
          {props.item.timestamp &&
            moment(props.item.timestamp).format("H:mm")
          }
        </div>
      </div>
    </div>
  )
}


class Chatdialogmedia extends React.Component {
  state = {
    loaded: false,
    error: false
  }

  render() {
    let width = null
    let height = null
    let media = null

    let item = this.props.item
    if(item.width) width = item.width
    if(item.height) height = item.height

    let chatMedia = "chat-media"
    if(this.props.smallDevice) chatMedia += " chat-media-smalldevice"

    if(item.mime && item.mime.includes("image")) {
      media =
            <img
                className={chatMedia}
                src={item.source}
                title={item.text}
                alt={item.text}
                width={width}
                height={height}
                style={!this.state.loaded ? { display: 'none' } : {}}
                onLoad={() => this.setState({loaded: true})}
                onError={() => this.setState({error: true})}
            />
    } else if (item.mime && item.mime.includes("video")) {
      media =
            <video
              className="chat-media"
              title={item.text}
              width={width}
              height={height}
              style={!this.state.loaded ? { display: 'none' } : {}}
              onLoadedData={() => this.setState({loaded: true})}
              onError={() => this.setState({error: true})}
              controls>
              <source src={item.source} type={item.mime} />
            </video>
    } else {
      if(!this.state.error) this.setState({error: true})
      console.log("mime type is missing or not supported:" +item.mime)
    }

    let container = "chat-container-left"
    if(this.props.item.byUser) {
      container = "chat-container-right"
    }

    // skip item on error
    if(this.state.error) {
      return null
    }
    // else
    return (
      <div className={container}>
        <div className={"chat-media-container"}>
          {media}
          {!this.state.loaded ? (
            <svg className="chat-media" width={width} height={height}>
              <rect width="100%" height="100%" fill="lightgrey"/>
            </svg>
          ) : null}
      </div>
    </div>
    )
  }
}


class Chatdialogselection extends React.Component {
  state = {
    hideSelection: true,
    hideEditButton: false,
    freetext: ""
  }

  toggleSelection = () => {
    this.setState({hideSelection: !this.state.hideSelection})
  }

  // hide selection and edit button if there is a next item
  componentDidMount() {
    if (this.props.nextItem === null) {
      this.setState({hideSelection: false, hideEditButton: true})
    }
    // get freetext from database
    this.setFreetext()
  }

  setFreetext = () => {
    if(Array.isArray(this.props.item.options) && this.props.item.options.length) {
      for(let option of this.props.item.options) {
        if(option && option.type === "free-text-reply") {
          this.setState({freetext: option.text})
          document.getElementById("freetextField").textContent = option.text
        }
      }
    }
  }

  handleChange = event => {
    let text = event.currentTarget.textContent
    // prevent line breaks
    if(text !== this.state.freetext) {
      this.setState({freetext: text})
    }
    // show placeholder
    if(text === "") {
      document.getElementById("freetextField").textContent = ""
    }
  }

  submitSelection = option => {
    if(option.type === "free-text-reply") {
      option.text = this.state.freetext
    }
    this.setState({hideSelection: true, hideEditButton: true})
    let messageId = this.props.item.id
    let channelIndex = this.props.channelIndex
    let channelId = this.props.activeChannel.id
    // // call selectOption to show selection and loading. trasmit data and wait for callback.
    this.props.selectOption(channelIndex, channelId, messageId, option, () => {
      this.setState({hideEditButton: false})
    })
  }

  render() {
    let nextItemText = null
    if(this.props.nextItem && this.props.nextItem.text) nextItemText = this.props.nextItem.text
    let options = this.props.item.options
    let selectionList = null
    if(Array.isArray(options) && options.length) {
      selectionList = options.map(option =>
        (option && option.id && option.text !== null && option.type) ?
          <li key={option.id}>
            {(option.type === "reply") ?
            <button className={"chat-selection-button " + (option.wasSelected ? "wasSelected " : "") + (nextItemText === option.text ? "selected" : "")} onClick={() => {this.submitSelection(option)}}>
              <div className="chat-selection-button-text">
                {addHtmlTags(option.text)}
              </div>
            </button>
            : (option.type === "free-text-reply") ?
              <div className="chat-selection-freetext-container">
                <TextareaFreetext freetext={this.state.freetext} submitSelection={this.submitSelection} handleChange={this.handleChange} addNewLine={this.addNewLine} autoGrow={this.autoGrow} resetFreetext={this.resetFreetext} option={option}/>
                <button className={this.state.freetext ? "chat-selection-freetext-button" : "chat-selection-freetext-button chat-selection-freetext-button-disabled"} onClick={() => {this.submitSelection(option)}}>
									<svg xmlns="http://www.w3.org/2000/svg" fill={this.state.freetext ? "#57a1caa1" : "rgba(0,0,0,0.2)"} width="20" height="20" viewBox="0 0 20 20"><path d="M1.675 17.5L19.17 10 1.675 2.5 1.7 8l12.5 1.7-12.5 1.7z"/></svg>
                </button>
              </div>
            :
            console.log("warning: missing or misspelled option type")
          }
          </li>
          :
          console.log("warning: missing option, option id or text in option " + this.props.item.id)
      )
    }

    // hide or show selection list depending on state of edit button
    let chatSelectionList = "chat-selection-list"
    if (this.state.hideSelection) {
      chatSelectionList += " hide"
    }

    // hide edit icon if there is no selection
    let chatSelectionEdit = "chat-selection-edit"
    if (this.state.hideEditButton) {
      chatSelectionEdit += " hide"
    }

    return (
      <div className="chat-container-left">
        <div className="chat-selection speech-bubble">
          <div className="chat-item-text">
            {this.props.item.text &&
              addHtmlTags(this.props.item.text)
            }
          </div>
          {selectionList &&
            <React.Fragment>
              <ul className={chatSelectionList}>
                {selectionList}
              </ul>
              <button className={chatSelectionEdit} onClick={this.toggleSelection}>
                •••
              </button>
            </React.Fragment>
          }
          <div className="chat-item-time">
            {this.props.item.timestamp &&
              moment(this.props.item.timestamp).format("H:mm")
            }
          </div>
        </div>
      </div>
    )
  }
}

const TextareaFreetext = props => {
  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      if(props.freetext === "") {
        event.preventDefault()
      } else {
        props.submitSelection(props.option)
      }
    }
  }
  return <div contentEditable="true" id="freetextField" className="chat-selection-freetext-field" onKeyDown={handleKeyDown} onInput={props.handleChange} placeholder="Write a message..."></div>
}

function addHtmlTags(string) {
  let t = string.split(/[<>]/)
  t = t.map((text, index) => {
    let tag = text.split(" ")

    let html = ""
    switch(tag[0]) {
      case "a":
        let href = getTagContent(tag, "href")
        if(href) html = <a href={href} target='_blank' rel="noopener noreferrer">{getHtmlContent(t, index, "/a")}</a>
        break
      case "br":
        html = <br />
        break
      case "em":
        html = <em>{getHtmlContent(t, index, "/em")}</em>
        break
      case "img":
        let src = getTagContent(tag, "src")
        let alt = getTagContent(tag, "alt")
        if(src && alt) html = <img className="emoji" src={src} alt={alt} />
        break
      case "strong":
        html = <strong>{getHtmlContent(t, index, "/strong")}</strong>
        break
      case "u":
        html = <u>{getHtmlContent(t, index, "/u")}</u>
        break
      default:
        html = text

      if(tag[0].startsWith("/")) {
        html = ""
      }
    }

    let ret =
      <React.Fragment key={`${text}-${index}`}>
        {html}
      </React.Fragment>
    return ret

  })
  return t
}

function getHtmlContent(t, index, tagEnd) {
  let ret = []
  for (let i = index; i < t.length; i++) {
    ret.push(t[i])
    if(t[i] === tagEnd) {
      t.splice(index, ret.length-1, ret)
      return ret.splice(1, ret.length-2)
    }
  }
  return ""
}

function getTagContent(tag, name) {
  let ret = []
  for(let i=1; i < tag.length; i++) {
      if(tag[i].startsWith(name)) {
        for(let j=i; j<tag.length; j++) {
          ret.push(tag[j])
          if(tag[j].endsWith("'")) {
            return ret.join(" ").match(/'([^']+)'/)[1]
          }
        }
      }
  }
  return null
}


export default Chats
